import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";

/**
 * Phase 10b: customer-facing list of attachable upcoming pickup bookings.
 *
 * Mirrors the listing endpoint's filter so the customer only sees bookings
 * they can actually attach to (no started tasks, not cancelled, future
 * scheduledDate, address matches the sender's saved AddressBook entry).
 *
 * Pass `addressBookEntryId` from the order flow's sender pick — server
 * resolves to the entry's underlying addressId and verifies ownership.
 */

export interface AttachablePickupBooking {
  slotId?: string;
  timingAvailability?: "CAPTURED" | "UNKNOWN_LEGACY";
  timeZone?: string | null;
  schedulingTimeZone?: string | null;
  id: string;
  pickupAddressId: string;
  pickupAddress: {
    id: string;
    line1: string;
    line2?: string | null;
    city: string;
    province: string;
    country: string;
    postalCode?: string | null;
  };
  scheduledDate: string;
  slot: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  } | null;
  dispatchCenter: {
    id: string;
    code: string;
    name: string;
    timeZone: string | null;
  };
  pickupService: { id: string; code: string; name: string };
  attachedShipmentCount: number;
  /** Fraction off the pickup fee the next shipment gets by joining this run
   * (0 = full price, 0.5 = 50% off…). Server computes from the tier curve. */
  nextBundleFractionOff: number;
  notes: string | null;
}

interface AttachableBookingsResponse {
  data: AttachablePickupBooking[];
}

interface UseAttachablePickupBookingsArgs {
  /** AddressBook entry ID picked as the sender. Required to enable the query. */
  addressBookEntryId?: string;
  shipmentIds?: string[];
}

export function useAttachablePickupBookings({
  addressBookEntryId,
  shipmentIds,
}: UseAttachablePickupBookingsArgs) {
  const { token, customer } = useSession();

  return useQuery<AttachableBookingsResponse>({
    queryKey: [
      "attachablePickupBookings",
      customer?.id,
      addressBookEntryId,
      shipmentIds,
    ],
    queryFn: async () => {
      const response = await axios.get<AttachableBookingsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/pickup-bookings`,
        {
          params: {
            view: "attachable",
            addressBookEntryId,
            shipmentIds: shipmentIds?.join(","),
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled:
      !!token &&
      !!customer?.id &&
      (!!addressBookEntryId || !!shipmentIds?.length),
    // Phase 10: balance "fresh enough that tab-switching is cheap" with
    // "server-side guard catches stale results on submit anyway."
    staleTime: 30_000,
  });
}
